import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLinkableProjects, useLinkProject, useUnlinkProject, type LinkableProject } from "@/hooks/useProjects";

interface Props {
  clientId: string;
  clientName: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** When set (Change/swap mode), this currently-linked project is unlinked before linking the chosen one. */
  replaceProjectId?: string;
}

export function LinkProjectDialog({ clientId, clientName, open, onOpenChange, replaceProjectId }: Props) {
  const [search, setSearch] = useState("");
  const [confirming, setConfirming] = useState<LinkableProject | null>(null);
  const { data: projects = [], isLoading } = useLinkableProjects(search);
  const link = useLinkProject(clientId);
  const unlink = useUnlinkProject(clientId);

  const close = () => {
    setSearch("");
    setConfirming(null);
    onOpenChange(false);
  };

  const doLink = async (projectId: string) => {
    if (replaceProjectId && replaceProjectId !== projectId) {
      await unlink.mutateAsync(replaceProjectId);
    }
    await link.mutateAsync(projectId);
    close();
  };

  const choose = (p: LinkableProject) => {
    if (p.companyId && p.companyId !== clientId) {
      setConfirming(p);
      return;
    }
    doLink(p.id);
  };

  const busy = link.isPending || unlink.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{replaceProjectId ? "Change linked project" : "Link existing project"}</DialogTitle>
        </DialogHeader>

        {confirming ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              "{confirming.name}" is currently linked to{" "}
              <span className="font-semibold">{confirming.clientName}</span>. Move it to{" "}
              <span className="font-semibold">{clientName}</span>?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirming(null)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={() => doLink(confirming.id)} disabled={busy}>
                Move it
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Input
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-900">
              {isLoading && (
                <div className="p-4 text-center text-sm text-slate-400">Loading...</div>
              )}
              {!isLoading && projects.length === 0 && (
                <div className="p-4 text-center text-sm text-slate-400">No projects found</div>
              )}
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={busy}
                  onClick={() => choose(p)}
                  className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-900 dark:bg-slate-950/40"
                >
                  <span className="font-medium text-slate-900 dark:text-slate-200">{p.name}</span>
                  <span className={`text-xs ${p.clientName ? "text-amber-600 dark:text-amber-400" : "text-slate-400"}`}>
                    {p.clientName ? `linked: ${p.clientName}` : "unlinked"}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={close} disabled={busy}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
